import { JSX } from 'react';
import { GetStaticProps } from 'next';
import { SitecorePageProps, ErrorPage, Page } from '@sitecore-content-sdk/nextjs';
import client from 'lib/sitecore-client';
import Layout from 'src/Layout';
import Providers from './Providers';
import scConfig from 'sitecore.config';
import components from '.sitecore/component-map';
import { getSiteSettings } from 'lib/utils/page-data-static-props/get-site-settings';
import { PageData } from 'lib/utils/page-data-static-props/get-page-data';
import {
  EMPTY_PAGE_DATA,
  PageDataProvider,
} from 'lib/utils/page-data-static-props/pageDataContext';
import Head from 'next/head';

export const ServerError = (): JSX.Element => (
  <>
    <Head>
      <title>500: Server Error</title>
    </Head>
    <div style={{ padding: 10 }}>
      <h1>500 Internal Server Error</h1>
      <p>There is a problem with the resource you are looking for, and it cannot be displayed.</p>
      <a href="/">Go to the Home page</a>
    </div>
  </>
);

// Extend SitecorePageProps to include our custom PageData that is passed to each route
interface ExtendedSitecorePageProps extends SitecorePageProps {
  customPageData?: PageData;
}

const Custom500 = (props: ExtendedSitecorePageProps): JSX.Element => {
  if (!(props && props.page)) {
    return <ServerError />;
  }

  return (
    <Providers componentProps={props.componentProps} page={props.page}>
      <PageDataProvider pageDataFromStaticProps={props.customPageData || EMPTY_PAGE_DATA}>
        <Layout page={props.page} />
      </PageDataProvider>
    </Providers>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const props: SitecorePageProps = {
    page: null,
  };

  let customPageData = {};
  // We want to fetch our error page regardless of whether GENERATE_STATIC_PATHS is true or false
  try {
    props.page = await client.getErrorPage(ErrorPage.InternalServerError, {
      site: scConfig.defaultSite,
      locale: context.locale || context.defaultLocale || scConfig.defaultLanguage,
    });
    // Fetch site settings so that we have the tokens required for Coveo search
    customPageData = await getSiteSettings({} as Page);
  } catch (error) {
    console.log('Error occurred while fetching error pages');
    console.log(error);
  }

  if (props.page) {
    props.componentProps = await client.getComponentData(props.page.layout, context, components);
    props.dictionary = await client.getDictionary({
      site: props?.page?.siteName,
      locale: props?.page?.locale,
    });
  }

  return {
    props: {
      ...props,
      customPageData,
    },
  };
};

export default Custom500;
